import { gql, useMutation, useQuery } from '@apollo/client';
import type { ApolloError } from '@apollo/client';
import { Badge, Callout, Select, Table, Text, TextField, Tooltip, Flex } from '@radix-ui/themes';
import { Button } from '@hubertai/hubert-component-library';
import { Info, Loader, Search } from 'lucide-react';
import { Form } from 'radix-ui';
import { use, useEffect, useMemo, useState } from 'react';
import type { FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import MultiSelect from '../elements/MultiSelect';
import LocaleContext from '../hooks/LocaleContext';
import RoleCards from './accessGroupsv2/elements/RoleCards';
import type { KeyedRole } from './accessGroupsv2/elements/roles';
import { viewerRoleId } from './accessGroupsv2/elements/roles';
import RemoveUserFromOrg from './elements/RemoveUserFromOrg';
import ResendInvitationEmail from './elements/ResendInvitationEmail';
import UserChangeLog from './elements/UserChangeLog';
import { CLIENT_MEMBERS, REMOVE_FROM_ORG } from './queries';

const INVITE_TO_ORG = gql`
  mutation InviteToOrganization(
    $firstName: String!
    $lastName: String!
    $email: String!
    $accessGroupIds: [String!]!
    $roleIds: [String!]
  ) {
    InviteToOrganization(
      firstName: $firstName
      lastName: $lastName
      email: $email
      accessGroupIds: $accessGroupIds
      roleIds: $roleIds
    )
  }
`;

const INVITE_FORM_DATA = gql`
  query InviteFormData {
    accessGroupsv2 {
      id
      name
    }
    allRoles: rolesv2 {
      id
      key
    }
  }
`;

const REINVITE = gql`
  mutation reinviteCandidates($email: String!) {
    ReinviteToOrganization(input: { email: $email })
  }
`;

const CHANGE_USER_PRIVILEGE = gql`
  mutation SetUserPrivilege($userId: String!, $privilege: UserPrivileges!) {
    setUserPrivilege(input: { userId: $userId, privilege: $privilege })
  }
`;

type SubmitData = {
  firstName: string;
  lastName: string;
  email: string;
};

type InviteFormData = {
  accessGroupsv2: { id: string; name: string }[];
  allRoles: KeyedRole[];
};

type SortField = 'name' | 'email' | 'role';
type SortOrder = 'asc' | 'desc';

type Member = {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  flag: string;
  isAdmin: boolean;
};

const MemberRow = ({
  user,
  removeFromOrg,
  removeFromOrgLoading,
  reinvite,
  reinviteLoading,
}: {
  user: Member;
  removeFromOrg: (user: { id: string }) => Promise<void>;
  removeFromOrgLoading: boolean;
  reinvite: (user: { email: string; firstName: string; lastName: string }) => Promise<void>;
  reinviteLoading: boolean;
}) => {
  const { t } = useTranslation();
  const { userSettings: loggedInUser } = use(LocaleContext);
  const [SetUserPrivilege] = useMutation(CHANGE_USER_PRIVILEGE);

  const [accessLevel, setAccessLevel] = useState(user.isAdmin ? 'admin' : 'user');

  const getDisplayLevel = (roleKey: string) => {
    if (roleKey === 'ag_super_admin') return t('settings.organization.accessLevel.agSuperAdmin');
    if (roleKey === 'admin') return t('settings.organization.accessLevel.admin');
    return t('settings.organization.accessLevel.user');
  };

  const handleChange = (value: string) => {
    SetUserPrivilege({
      variables: {
        userId: user.id,
        privilege: value.toUpperCase(),
      },
    })
      .then(() => {
        toast.success(t('settings.organization.toast.accessLevelUpdated'), {
          description: t('settings.organization.toast.accessLevelUpdatedDescription', {
            userName: `${user.firstName} ${user.lastName}`,
            newLevel: getDisplayLevel(value),
          }),
        });
      })
      .catch(() => {
        toast.error(t('settings.organization.toast.accessLevelUpdateFailed'), {
          description: t('settings.organization.toast.accessLevelUpdateFailedDescription', {
            userName: `${user.firstName} ${user.lastName}`,
            newLevel: getDisplayLevel(value),
          }),
        });
      });
    setAccessLevel(value);
  };

  return (
    <Table.Row align="center">
      <Table.Cell>
        <NamePill user={user} />
      </Table.Cell>
      <Table.Cell>
        {user.firstName} {user.lastName}{' '}
        {user.flag === 'unconfirmed' && (
          <Badge variant="soft" color="amber" radius="large">
            {t('settings.organization.notActivated')}
          </Badge>
        )}
      </Table.Cell>
      <Table.Cell>{user.email}</Table.Cell>
      <Table.Cell>
        {user.id !== loggedInUser.id && (
          <Select.Root value={accessLevel} onValueChange={handleChange}>
            <Select.Trigger className="w-full" />
            <Select.Content>
              <Select.Item value="admin">{t('settings.organization.accessLevel.admin')}</Select.Item>
              <Select.Item value="user">{t('settings.organization.accessLevel.user')}</Select.Item>
            </Select.Content>
          </Select.Root>
        )}
      </Table.Cell>
      <Table.Cell align="right" className="">
        <div className="flex items-center justify-end gap-3">
          <UserChangeLog user={user} />
          {user.flag === 'unconfirmed' && (
            <ResendInvitationEmail user={user} loading={reinviteLoading} onSubmit={reinvite} />
          )}
          {user.id !== loggedInUser.id && (
            <RemoveUserFromOrg user={user} loading={removeFromOrgLoading} onSubmit={removeFromOrg} />
          )}
        </div>
      </Table.Cell>
    </Table.Row>
  );
};

const Organization = () => {
  const { t } = useTranslation();
  const [InviteToOrganization, { loading }] = useMutation(INVITE_TO_ORG);
  const [ReinviteToOrganization, { loading: reinviteLoading }] = useMutation(REINVITE);
  const [RemoveFromOrganization, { loading: removeFromOrgLoading }] = useMutation(REMOVE_FROM_ORG, {
    update(cache, res) {
      if (res.data.RemoveUserFromOrganization.__typename === 'User') {
        cache.evict({
          id: cache.identify(res.data.RemoveUserFromOrganization),
        });
      }
    },
  });
  const {
    data: clientMembersData,
    loading: clientMembersLoading,
    refetch,
  } = useQuery<{
    viewer: {
      client: {
        members: {
          nodes: Member[];
        };
      };
    };
  }>(CLIENT_MEMBERS, { fetchPolicy: 'cache-first' });

  const { data: inviteFormData } = useQuery<InviteFormData>(INVITE_FORM_DATA);

  const [formKey, setFormKey] = useState(0);
  const [selectedAccessGroupIds, setSelectedAccessGroupIds] = useState<string[]>([]);
  const [selectedRoleId, setSelectedRoleId] = useState('');

  const [searchQuery, setSearchQuery] = useState('');
  const [sortField, setSortField] = useState<SortField>('name');
  const [sortOrder, setSortOrder] = useState<SortOrder>('asc');

  const defaultAccessGroupId = inviteFormData?.accessGroupsv2?.find((g) => g.name === 'Default Access Group')?.id;

  const defaultRoleId = viewerRoleId(inviteFormData?.allRoles ?? []);

  useEffect(() => {
    if (defaultAccessGroupId && selectedAccessGroupIds.length === 0) {
      setSelectedAccessGroupIds([defaultAccessGroupId]);
    }
    if (defaultRoleId && !selectedRoleId) {
      setSelectedRoleId(defaultRoleId);
    }
  }, [defaultAccessGroupId, defaultRoleId]);

  const accessGroupOptions = (inviteFormData?.accessGroupsv2 ?? []).map((g) => ({ label: g.name, value: g.id }));

  const filteredAndSortedMembers = useMemo(() => {
    if (!clientMembersData?.viewer.client.members.nodes) return [];

    let members = [...clientMembersData.viewer.client.members.nodes];

    if (searchQuery.trim()) {
      const lowerQuery = searchQuery.toLowerCase();
      members = members.filter(
        (m) =>
          m.firstName.toLowerCase().includes(lowerQuery) ||
          m.lastName.toLowerCase().includes(lowerQuery) ||
          m.email.toLowerCase().includes(lowerQuery),
      );
    }

    members.sort((a, b) => {
      let comparison = 0;
      if (sortField === 'name') {
        comparison = a.lastName.toLowerCase().localeCompare(b.lastName.toLowerCase());
      } else if (sortField === 'email') {
        comparison = a.email.localeCompare(b.email);
      } else if (sortField === 'role') {
        const roleA = a.isAdmin ? 1 : 0;
        const roleB = b.isAdmin ? 1 : 0;
        comparison = roleA - roleB;
      }
      return sortOrder === 'asc' ? comparison : -comparison;
    });

    return members;
  }, [clientMembersData, searchQuery, sortField, sortOrder]);

  const handleSubmit = async (formEvent: FormEvent<HTMLFormElement>) => {
    formEvent.preventDefault();

    const form = formEvent.currentTarget;
    const formData = Object.fromEntries(new FormData(form)) as unknown as SubmitData;
    const firstName = formData.firstName.trim();
    const lastName = formData.lastName.trim();
    const email = formData.email.trim().toLowerCase();

    try {
      if (selectedAccessGroupIds.length === 0) {
        toast.error(t('settings.organization.toast.selectAtLeastOneAccessGroup'));
        return;
      }
      if (!selectedRoleId) {
        toast.error(t('settings.organization.toast.selectAtLeastOneRole'));
        return;
      }

      await InviteToOrganization({
        variables: {
          firstName,
          lastName,
          email,
          accessGroupIds: selectedAccessGroupIds,
          roleIds: [selectedRoleId],
        },
      });

      refetch();
      form.reset();
      setFormKey((k) => k + 1);
      setSelectedAccessGroupIds(defaultAccessGroupId ? [defaultAccessGroupId] : []);
      setSelectedRoleId(defaultRoleId);
    } catch (err) {
      const code = (err as ApolloError).graphQLErrors?.[0]?.extensions?.code;
      if (code === 'AlreadyExists') {
        toast.error(t('settings.organization.toast.emailAlreadyExists'), {
          description: t('settings.organization.toast.emailAlreadyExistsDescription', { email }),
        });
      } else {
        toast.error(t('settings.organization.toast.couldNotInvite'));
      }
    }
  };

  const reinviteUser = (user: { email: string; firstName: string; lastName: string }) => {
    return ReinviteToOrganization({
      variables: {
        email: user.email,
      },
    })
      .then(() => {
        toast.success(t('settings.organization.toast.activationEmailSent'), {
          description: t('settings.organization.toast.activationEmailSentDescription', {
            userName: `${user.firstName} ${user.lastName}`,
          }),
        });
      })
      .catch(() => {
        toast.error(t('settings.organization.toast.couldNotResendInvitation'));
      });
  };

  const removeUserFromOrg = (user: { id: string }) => {
    return RemoveFromOrganization({
      variables: {
        userId: user.id,
      },
    })
      .then(() => {
        refetch();
      })
      .catch(() => {
        refetch();
      });
  };

  return (
    <div className="flex flex-col max-w-screen-md">
      <div className="border border-gray-200 rounded mb-0 mt-6">
        <Form.Root onSubmit={handleSubmit} key={`form-${formKey}`} className="p-4 flex flex-col w-full">
          <h2 className="truncate pb-4">{t('settings.organization.inviteHeading')}</h2>
          <Form.Field name="firstName" className="mb-4">
            <div className="flex items-baseline justify-between">
              <Form.Label asChild>
                <Text size="2">
                  <strong>{t('settings.organization.form.firstNameLabel')}</strong>
                </Text>
              </Form.Label>
              <Text size="1" color="red">
                <Form.Message match="valueMissing">{t('settings.organization.form.firstNameRequired')}</Form.Message>
              </Text>
            </div>
            <Form.Control asChild>
              <TextField.Root
                placeholder={t('settings.organization.form.firstNamePlaceholder')}
                type="text"
                required
                className="w-full"
                size="3"
              />
            </Form.Control>
          </Form.Field>
          <Form.Field name="lastName" className="mb-4">
            <div className="flex items-baseline justify-between">
              <Form.Label asChild>
                <Text size="2">
                  <strong>{t('settings.organization.form.lastNameLabel')}</strong>
                </Text>
              </Form.Label>
              <Text size="1" color="red">
                <Form.Message match="valueMissing">{t('settings.organization.form.lastNameRequired')}</Form.Message>
              </Text>
            </div>
            <Form.Control asChild>
              <TextField.Root
                placeholder={t('settings.organization.form.lastNamePlaceholder')}
                type="text"
                required
                className="w-full"
                size="3"
              />
            </Form.Control>
          </Form.Field>
          <Form.Field name="email" className="mb-4">
            <div className="flex items-baseline justify-between">
              <Form.Label asChild>
                <Text size="2">
                  <strong>{t('settings.organization.form.emailLabel')}</strong>
                </Text>
              </Form.Label>
              <Text size="1" color="red">
                <Form.Message match="valueMissing">{t('settings.organization.form.emailRequired')}</Form.Message>
                <Form.Message match="typeMismatch">{t('settings.organization.form.emailInvalid')}</Form.Message>
              </Text>
            </div>
            <Form.Control asChild>
              <TextField.Root
                placeholder={t('settings.organization.form.emailPlaceholder')}
                type="email"
                required
                className="w-full"
                size="3"
              />
            </Form.Control>
          </Form.Field>
          <div className="mb-4">
            <Text size="2">
              <strong>{t('settings.organization.form.accessGroupsLabel')}</strong>
            </Text>
            <div className="mt-1">
              {accessGroupOptions.length > 0 ? (
                <MultiSelect
                  key={`access-groups-${formKey}-${defaultAccessGroupId ?? 'none'}`}
                  options={accessGroupOptions}
                  preSelected={defaultAccessGroupId ? [defaultAccessGroupId] : []}
                  onChange={setSelectedAccessGroupIds}
                  placeholder={t('settings.organization.form.accessGroupsPlaceholder')}
                  className="w-full"
                />
              ) : (
                <Callout.Root color="blue">
                  <Callout.Text className="mb-0">{t('settings.accessGroup.emptyState')}</Callout.Text>
                </Callout.Root>
              )}
            </div>
            {selectedAccessGroupIds.length > 1 && (
              <Callout.Root color="amber" size="1" className="mt-2">
                <Callout.Icon>
                  <Info size={16} />
                </Callout.Icon>
                <Callout.Text>
                  {t('settings.organization.multipleGroupsNote', { pageName: t('settings.nav.accessGroupsv2') })}
                </Callout.Text>
              </Callout.Root>
            )}
          </div>
          <div className="mb-4">
            <Text size="2">
              <strong>{t('settings.organization.form.rolesLabel')}</strong>
            </Text>
            <div className="mt-1">
              <RoleCards
                roles={inviteFormData?.allRoles ?? []}
                value={selectedRoleId}
                onValueChange={setSelectedRoleId}
                canAssignSuperAdmin
              />
            </div>
          </div>
          <Form.Submit asChild>
            <Button size="3" color="grass" className="w-1/2" variant="solid" loading={loading} disabled={loading}>
              {t('settings.organization.inviteButton')}
            </Button>
          </Form.Submit>
        </Form.Root>
      </div>
      <div className="border border-gray-200 rounded mb-0 mt-6">
        <div className="p-4">
          <Flex justify="between" align="center" mb="4">
            <h2 className="truncate">{t('settings.organization.membersHeading')}</h2>
            <Flex gap="3" align="center">
              <TextField.Root
                placeholder={t('common.search', 'Search...')}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              >
                <TextField.Slot>
                  <Search height="16" width="16" />
                </TextField.Slot>
              </TextField.Root>
              <Select.Root
                value={`${sortField}-${sortOrder}`}
                onValueChange={(val) => {
                  const parts = val.split('-');
                  setSortField(parts[0] as SortField);
                  setSortOrder(parts[1] as SortOrder);
                }}
              >
                <Select.Trigger />
                <Select.Content>
                  <Select.Group>
                    <Select.Label>{t('common.sort', 'Sort by')}</Select.Label>
                    <Select.Item value="name-asc">{t('settings.sort.nameAsc', 'Name (A-Z)')}</Select.Item>
                    <Select.Item value="name-desc">{t('settings.sort.nameDesc', 'Name (Z-A)')}</Select.Item>
                    <Select.Item value="email-asc">{t('settings.sort.emailAsc', 'Email (A-Z)')}</Select.Item>
                    <Select.Item value="email-desc">{t('settings.sort.emailDesc', 'Email (Z-A)')}</Select.Item>
                    <Select.Item value="role-desc">
                      {t('settings.sort.accessLevelDesc', 'Access level (Admin first)')}
                    </Select.Item>
                    <Select.Item value="role-asc">
                      {t('settings.sort.accessLevelAsc', 'Access level (User first)')}
                    </Select.Item>
                  </Select.Group>
                </Select.Content>
              </Select.Root>
            </Flex>
          </Flex>
          {clientMembersLoading && (
            <div className="flex justify-center my-4">
              <Loader className="animate-spin" />
            </div>
          )}
          <Table.Root size="1">
            <Table.Body>
              {filteredAndSortedMembers.map((user, index) => (
                <MemberRow
                  user={user}
                  removeFromOrg={removeUserFromOrg}
                  removeFromOrgLoading={removeFromOrgLoading}
                  reinvite={reinviteUser}
                  reinviteLoading={reinviteLoading}
                  key={user.id || index}
                />
              ))}
            </Table.Body>
          </Table.Root>
        </div>
      </div>
    </div>
  );
};

export default Organization;

const NamePill = ({ user }: { user: { id: string; firstName: string; lastName: string } }) => {
  const colors = ['green', 'purple', 'grey', 'red', 'yellow', 'blue'];

  return (
    <Tooltip content={<Text size="3">{`${user.firstName} ${user.lastName}`}</Text>}>
      <span className={'name-pill ' + colors[1]}>
        {user.firstName.slice(0, 1).toUpperCase() + user.lastName.slice(0, 1).toUpperCase()}
      </span>
    </Tooltip>
  );
};
